import { XProvider } from '@ant-design/x';
import { App, theme, Tooltip } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import enUs from 'antd/lib/locale/en_US';
import { useColorScheme } from '../../../hooks/useColorScheme';
import { useOnBehalfOf } from '../../../hooks/useOnBehalfOf';
import { useAppStore } from '../../../store/store';
import { getUserName, lightenHex } from '../../../utils/utils';
import { Icon } from '../Icon';

/**
 * Root Ant Design / Ant Design X configuration: theme, locale, modal defaults, and on-behalf banner.
 */

export default function AntdConfigProvider({ children }: { children: React.ReactNode }) {
  const brandingData = useAppStore((s) => s.brandingData);
  const { resolvedColorScheme } = useColorScheme();
  const { isOnBehalfOfUser, onBehalfOfUser, exitOnBehalfOfUser } = useOnBehalfOf();
  const { t } = useTranslation();
  const mainButtonsColorBackground = brandingData?.mainButtonsColorBackground || '#193057';
  const mainButtonsColorBackgroundHover = lightenHex(mainButtonsColorBackground, 12);

  const mainButtonsColorForeground = brandingData?.mainButtonsColorForeground || '#ffffff';
  const isDarkMode = resolvedColorScheme === 'dark';
  // Ant Design palette generation needs parseable hex seeds (CSS vars resolve to #000).
  // Light: brand navy (--color-blue-dark). Dark: azure so Spin/Progress stay visible on dark surfaces.
  const colorPrimary = isDarkMode ? '#0078a8' : '#193057';
  // Info Alert derives colorInfoBg from colorInfo. Navy (#193057) yields
  // ~#8d9296 bg + dark text (unreadable). Azure keeps light tint + readable text.
  const colorInfo = '#0078a8';

  const antTheme = useMemo(
    () => ({
      algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: {
        colorPrimary,
        colorInfo,
        colorSuccess: '#00b048',
        colorError: '#f73b3b',
        colorErrorHover: 'var(--color-red-hover)',
        colorErrorActive: 'var(--color-red-dark)',
        // Hex seeds only: antd's palette math turns CSS vars into #000 (see colorPrimary above).
        // Light navy / dark near-white match --color-blue-dark in tailwind.css.
        colorLink: isDarkMode ? '#e8ecf4' : '#193057',
        colorLinkHover: '#0078a8',
        colorLinkActive: isDarkMode ? '#0087c3' : '#06416a',
        borderRadius: 5,
        wireframe: true,
        ...(isDarkMode
          ? {
              colorBgContainer: 'var(--color-white)',
              colorBgElevated: 'var(--color-white)',
              colorBgLayout: 'var(--color-background)',
              colorBorder: 'var(--color-gray-medium)',
              colorText: '#e8ecf4',
              colorTextDescription: '#a8aeb8',
              colorTextHeading: '#e8ecf4',
            }
          : {}),
      },
      components: {
        Table: {
          lineHeight: 1,
          bodySortBg: 'rgb(255,255,255,0)',
          padding: 14,
          rowSelectedBg: isDarkMode ? 'rgba(232,236,244,0.12)' : 'rgba(25,48,87,0.1)',
          rowSelectedHoverBg: isDarkMode ? 'rgba(232,236,244,0.2)' : 'rgba(25,48,87,0.2)',
          motionDurationMid: '0s',
          motionDurationSlow: '0s',
          rowHoverBg: 'var(--color-files-highlight)',
          // Dark: colorBgContainer is a CSS var; antd FastColor.onBackground → #000 headerBg.
          ...(isDarkMode
            ? {
                headerBg: 'var(--color-white)',
                footerBg: 'var(--color-white)',
              }
            : {}),
        },
        Select: {
          optionSelectedColor: isDarkMode ? 'var(--color-blue-dark)' : 'rgb(0,0,0)',
          optionSelectedBg: isDarkMode ? 'rgba(232,236,244,0.12)' : 'rgba(25,48,87,0.1)',
          activeOutlineColor: 'rgba(255,255,255,0)',
        },
        TreeSelect: {
          nodeSelectedBg: isDarkMode ? 'rgba(232,236,244,0.12)' : 'rgb(25,48,87,0.1)',
        },
        Input: {
          activeShadow: '0 0 0 0 rgba(255,255,255,0)',
          errorActiveShadow: '0 0 0 0 rgba(255,255,255,0)',
          warningActiveShadow: '0 0 0 0 rgba(255,255,255,0)',
        },
        Button: {
          colorPrimary: mainButtonsColorBackground,
          defaultShadow: isDarkMode ? '0 1px 0 rgba(0,0,0,0.2)' : '0 1px 0 rgba(0,0,0,0.01)',
          primaryShadow: '0 1px 0 rgba(5,145,255,0.1)',
          dangerShadow: '0 1px 0 rgba(255,38,5,0.06)',
          colorPrimaryHover: mainButtonsColorBackgroundHover,
          primaryColor: mainButtonsColorForeground,
        },
        // ponytail: List tokens remain until FilesList grid migrates off List.
        List: {
          descriptionFontSize: 10,
          titleMarginBottom: 2,
        },
        // Match form List.Item `px-0! py-2.5!` (10px block, 0 inline) vs Listy defaults 12/16.
        Listy: {
          itemPaddingBlock: 10,
          itemPaddingInline: 0,
        },
        Modal: {
          paddingLG: 18,
          padding: 10,
        },
        Popover: {
          colorBgElevated: 'var(--color-white)',
        },
        Progress: {
          defaultColor: colorPrimary,
        },
        Spin: {
          colorPrimary,
        },
        Tooltip: {
          colorBgSpotlight: isDarkMode ? 'var(--color-white)' : 'rgba(0, 0, 0, 0.85)',
        },
        Tabs: {
          inkBarColor: isDarkMode ? 'var(--color-black)' : 'var(--color-blue-dark)',
          itemColor: isDarkMode ? 'var(--color-gray-dark)' : 'rgba(0, 0, 0, 0.65)',
          itemHoverColor: 'var(--color-blue-dark)',
          itemSelectedColor: isDarkMode ? 'var(--color-black)' : 'var(--color-blue-dark)',
        },
      },
    }),
    [
      colorPrimary,
      isDarkMode,
      mainButtonsColorBackground,
      mainButtonsColorBackgroundHover,
      mainButtonsColorForeground,
    ],
  );

  const ModalDefaults = () => {
    const { modal } = App.useApp();
    const isConfiguredRef = useRef(false);

    useEffect(() => {
      if (isConfiguredRef.current) return;

      const wrapModalFunc = (fn: any) => {
        return (config: any = {}) => {
          const { maskClosable, ...restConfig } = config;
          const mergedConfig = {
            closable: config.closable ?? true,
            centered: config.centered ?? true,
            width: config.width ?? 600,
            destroyOnHidden: config.destroyOnHidden ?? true,
            mask: {
              closable: config.mask?.closable ?? maskClosable ?? true,
              ...(config.mask ?? {}),
            },
            ...restConfig,
          };

          if (mergedConfig.icon === undefined) {
            mergedConfig.icon = null;
          }

          return fn(mergedConfig);
        };
      };

      (modal as any).confirm = wrapModalFunc(modal.confirm);
      (modal as any).info = wrapModalFunc(modal.info);
      (modal as any).success = wrapModalFunc(modal.success);
      (modal as any).error = wrapModalFunc(modal.error);
      (modal as any).warning = wrapModalFunc(modal.warning);

      isConfiguredRef.current = true;
    }, [modal]);

    return null;
  };

  return (
    <XProvider locale={enUs} theme={antTheme}>
      <App className="h-full">
        <ModalDefaults />
        {isOnBehalfOfUser && (
          <>
            <div className="border-red-dark pointer-events-none fixed inset-0 z-100 rounded-sm border-4 p-2" />
            {/* On Behalf Of Notch */}
            {onBehalfOfUser && (
              <div className="from-red-dark/70 to-red-dark fixed bottom-0 left-1/2 z-100 -translate-x-1/2 rounded-t-lg bg-linear-to-b px-4 py-2 text-white shadow-lg backdrop-blur-sm">
                <div className="relative flex items-center justify-center">
                  <div className="flex flex-col items-center px-4">
                    <span className="text-sm font-medium">
                      {getUserName(
                        onBehalfOfUser.firstName,
                        onBehalfOfUser.lastName,
                        onBehalfOfUser.email,
                      )}
                    </span>
                    {onBehalfOfUser.email !==
                      getUserName(
                        onBehalfOfUser.firstName,
                        onBehalfOfUser.lastName,
                        onBehalfOfUser.email,
                      ) && <span className="text-xs opacity-90">{onBehalfOfUser.email}</span>}
                  </div>
                  <Tooltip title={t('common.stopImpersonation') || 'Stop Impersonation'}>
                    <button
                      onClick={async () => {
                        try {
                          await exitOnBehalfOfUser();
                        } catch (error) {
                          console.error('Failed to exit on behalf of user:', error);
                        }
                      }}
                      className="hover:bg-red-dark absolute -top-1.5 -right-3 flex items-center justify-center rounded p-1 transition-colors"
                      aria-label={t('common.stopImpersonation') || 'Stop Impersonation'}
                    >
                      <Icon name="XIcon" className="h-4 w-4" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            )}
          </>
        )}
        {children}
      </App>
    </XProvider>
  );
}
